import { type CSSProperties, Suspense, useEffect } from 'react'
import {
  faAnglesLeft,
  faAnglesRight,
  faBars,
  faBoxArchive,
  faClockRotateLeft,
  faEnvelope,
  faFileLines,
  faGauge,
  faGlobe,
  faImages,
  faMoon,
  faPlug,
  faRightFromBracket,
  faShapes,
  faSun,
  faUsers,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons'
import {
  Link,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
} from 'react-router'

import { useAdminExtensions, useLocales } from '../../extensions/hooks'
import { logout } from '../../features/auth'
import { useAuthStore } from '../../features/auth/hooks'
import { NotificationBell } from '../../features/notifications'
import { IconButton, LanguageSelect, Loading } from '../../shared/components'
import { resolveTheme, useUiStore } from '../../shared/hooks'
import type { Messages } from '../../shared/i18n'
import { useT } from '../../shared/i18n'
import { pluginIcon } from '../../shared/icons'
import { bandTheme } from '../../shared/theme'

import { useSidebarResize } from './hooks'
import type { NavItem, NavSection } from './Shell.types'
import { SidebarNav } from './SidebarNav'
import { SidebarResizer } from './SidebarResizer'

/** The theme's default sidebar width (`--sidebar-width`, ADR-056). */
const themeSidebarWidth = () =>
  parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(
      '--sidebar-width',
    ),
  ) || 248

const contentNav = (t: Messages): NavItem[] => [
  { to: '/', label: t.nav.dashboard, icon: faGauge, end: true },
  {
    to: '/content',
    label: t.nav.content,
    icon: faFileLines,
    permission: 'content.entry.read',
  },
  {
    to: '/media',
    label: t.nav.media,
    icon: faImages,
    permission: 'media.read',
  },
]

const buildNav = (t: Messages): NavItem[] => [
  {
    to: '/content-types',
    label: t.nav.contentTypes,
    icon: faShapes,
    permission: 'content.type.read',
  },
  {
    to: '/plugins',
    label: t.nav.plugins,
    icon: faPlug,
    permission: 'plugin.read',
  },
]

const adminNav = (t: Messages): NavItem[] => [
  { to: '/users', label: t.nav.users, icon: faUsers, permission: 'user.read' },
  {
    to: '/roles',
    label: t.nav.roles,
    icon: faUserShield,
    permission: 'user.read',
  },
  {
    to: '/audit',
    label: t.nav.audit,
    icon: faClockRotateLeft,
    permission: 'audit.read',
  },
  {
    to: '/website',
    label: t.nav.website,
    icon: faGlobe,
    permission: 'site.settings.manage',
  },
  {
    to: '/mail',
    label: t.nav.mail,
    icon: faEnvelope,
    permission: 'mail.settings.manage',
  },
  {
    to: '/backup',
    label: t.nav.backup,
    icon: faBoxArchive,
    permission: 'backup.export',
  },
]

function Breadcrumbs() {
  const matches = useMatches()
  const { t } = useT()

  const crumbs = matches
    .map((m) => ({
      path: m.pathname,
      crumb: (m.handle as { crumb?: string } | undefined)?.crumb,
    }))
    .filter((c): c is { path: string; crumb: string } => !!c.crumb)

  return (
    <nav className="topbar__crumbs" aria-label={t.common.breadcrumb}>
      <Link to="/">GX-CMS</Link>
      {crumbs.map((c) => (
        <span key={c.path} className="row" style={{ gap: 8 }}>
          <span aria-hidden>/</span>
          <Link to={c.path}>
            {t.crumbs[c.crumb as keyof Messages['crumbs']] ?? c.crumb}
          </Link>
        </span>
      ))}
    </nav>
  )
}

export function Shell() {
  const user = useAuthStore((s) => s.user)
  const { theme, design, setTheme, navOpen, setNavOpen } = useUiStore()
  const extensions = useAdminExtensions()
  const { locales } = useLocales()
  const { t } = useT()
  const navigate = useNavigate()
  const location = useLocation()
  const resolved = resolveTheme(theme)
  // GXLabs and the colourful themes keep a dark band; Apple follows the appearance (ADR-056, ADR-068).
  const sidebarTheme = bandTheme(design, resolved)

  const resize = useSidebarResize(themeSidebarWidth)

  useEffect(() => setNavOpen(false), [location.pathname, setNavOpen])

  // One sidebar group per plugin, headed by the plugin's own icon and name (labels localized, ADR-044).
  const pluginGroups = extensions.plugins
    .map((p) => ({
      plugin: p,
      items: extensions.navigation
        .filter((n) => n.pluginKey === p.key)
        .map(
          (n): NavItem => ({
            to: `/extensions/${encodeURIComponent(n.pluginKey)}/${encodeURIComponent(n.page)}`,
            label: n.label,
            icon: pluginIcon(n.icon ?? p.icon),
          }),
        ),
    }))
    .filter((g) => g.items.length)

  const permissions = useAuthStore((s) => s.permissions)

  // Core groups and one per plugin; links the user may not open are left out.
  const sections: NavSection[] = [
    { id: 'core.content', title: t.nav.groupContent, items: contentNav(t) },
    ...pluginGroups.map((g) => ({
      id: `plugin.${g.plugin.key}`,
      title: g.plugin.name,
      icon: pluginIcon(g.plugin.icon),
      items: g.items,
    })),
    { id: 'core.build', title: t.nav.groupBuild, items: buildNav(t) },
    { id: 'core.admin', title: t.nav.groupAdmin, items: adminNav(t) },
  ]
    .map((s) => ({
      ...s,
      items: s.items.filter(
        (i) => !i.permission || permissions.has(i.permission),
      ),
    }))
    .filter((s) => s.items.length)

  return (
    <div
      className="shell"
      data-nav-open={navOpen}
      data-nav-collapsed={resize.collapsed || undefined}
      style={
        resize.custom
          ? ({ '--sidebar-custom': `${resize.width}px` } as CSSProperties)
          : undefined
      }
    >
      <aside
        className="sidebar"
        data-theme={sidebarTheme}
        aria-label={t.shell.mainNavigation}
      >
        <Link to="/" className="sidebar__brand" aria-label={t.shell.home}>
          <img
            src={`/brand/gxlabs-wordmark-${sidebarTheme === 'dark' ? 'white' : 'black'}.png`}
            alt="GXLabs"
          />
          <span className="sidebar__product">CMS</span>
        </Link>
        <SidebarNav sections={sections} rail={resize.collapsed} />
        <div className="sidebar__footer">
          <Link
            to="/settings"
            className="sidebar__user"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <p className="sidebar__user-name" title={user?.displayName}>
              {user?.displayName}
            </p>
            <p className="sidebar__user-meta">
              {user?.roles.map((r) => r.name).join(', ') || t.shell.noRole}
            </p>
          </Link>
          <IconButton
            icon={resize.collapsed ? faAnglesRight : faAnglesLeft}
            label={
              resize.collapsed
                ? t.shell.expandNavigation
                : t.shell.collapseNavigation
            }
            onClick={resize.toggle}
          />
          <IconButton
            icon={resolved === 'dark' ? faSun : faMoon}
            label={resolved === 'dark' ? t.shell.lightTheme : t.shell.darkTheme}
            onClick={() => setTheme(resolved === 'dark' ? 'light' : 'dark')}
          />
          <IconButton
            icon={faRightFromBracket}
            label={t.shell.signOut}
            onClick={async () => {
              await logout()
              navigate('/login')
            }}
          />
        </div>
        <SidebarResizer resize={resize} label={t.shell.resizeNavigation} />
      </aside>
      <div className="main">
        <header className="topbar">
          <span className="mobile-nav-toggle">
            <IconButton
              icon={faBars}
              label={t.shell.openNavigation}
              onClick={() => setNavOpen(!navOpen)}
            />
          </span>
          <Breadcrumbs />
          <NotificationBell onOpenLink={(link) => navigate(link)} />
          {locales.length > 1 && <LanguageSelect locales={locales} compact />}
        </header>
        <main className="content" id="main">
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  )
}
